import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import pLimit from 'p-limit';

import type { Category, Item, Schema } from '../../../models';
import type { AssetForm } from '../../forms';
import type { ChannelForm } from '../../forms/Channel/ChannelForm';
import type { LiveProgramForm } from '../../forms/LiveProgram/LiveProgramForm';
import type { RootState } from '..';
import { processSaveAssetJob } from './processSaveAssetJob';
import { processSaveChannelThumbnailJob } from './processSaveChannelThumbnailJob';
import { processSaveExternalSubtitleJob } from './processSaveExternalSubtitleJob';
import { processSaveLiveProgramThumbnailJob } from './processSaveLiveProgramThumbnailJob';
import { SaveAssetJob } from './SaveAssetJob';

export type SaveAssetJobsState = {
  arePopupsShowing: boolean;
  jobs: Array<SaveAssetJob>;
  jobType: string;
  batchUploadEnable: boolean;
  ingestInProgress: boolean;
};

export interface StartJobPayload {
  jobType: string;
  form: AssetForm | ChannelForm | LiveProgramForm;
  extraParams?: StartJobPayloadExtraParams;
}

export interface BatchUploadJobPayload {
  jobType: string;
  forms: AssetForm[];
  extraParams?: StartJobPayloadExtraParams;
}

export interface StartJobPayloadExtraParams {
  languageCode?: string;
  returnItemCallback?: (clip: Item, index: number) => void;
}

export const initialState: SaveAssetJobsState = {
  arePopupsShowing: false,
  jobs: [],
  jobType: '',
  batchUploadEnable: false,
  ingestInProgress: false,
};
const MULTIPART_LIMIT = 100000000; // 100 MB
const MULTIPART_CHUNK_SIZE = 100000000; // 100 MB
const SaveAssetJobsSlice = createSlice({
  name: 'saveAssetJobs',
  initialState,
  reducers: {
    popupsShowing: (state, action) => {
      // console.log('action', action.payload);
      state.arePopupsShowing = action.payload;
    },
    updateBatchUploadEnable: (state, action) => {
      state.batchUploadEnable = action.payload;
    },
    updateIngestInProgress: (state, action) => {
      state.ingestInProgress = action.payload;
    },
    addJob: (state, action) => {
      state.jobs.push(action.payload);
    },
    removeJob: (state, action) => {
      const index = state.jobs.indexOf(action.payload);
      state.jobs.splice(index, 1);
    },
    updateJob: (state, action) => {
      const index = state.jobs.indexOf(action.payload);
      const { jobs } = state;
      jobs.splice(index, 1);
      jobs.push(action.payload);
      state.jobs = jobs;
    },
    clearJobs: (state) => {
      state.jobs = [];
      state.arePopupsShowing = false;
    },
    closePopup: (state, action) => {
      // state.arePopupsShowing = false;
      const index = action.payload;
      const jobs = cloneDeep(state.jobs);
      const newJob = jobs[index];
      newJob.isPopupOpen = false;
      state.jobs = jobs;
    },
  },
  extraReducers: {},
});

export const {
  popupsShowing,
  updateIngestInProgress,
  addJob,
  clearJobs,
  removeJob,
  updateJob,
  closePopup,
  updateBatchUploadEnable,
} = SaveAssetJobsSlice.actions;

// export SaveAssetJobsSlice selector to get the slice in any component
export const SaveAssetJobsSelector = (state: RootState) => state.saveAssetJobs;
// export The reducer
const SaveAssetJobsReducer = SaveAssetJobsSlice.reducer;
export default SaveAssetJobsReducer;

async function processSaveAssetJob1(job: SaveAssetJob, schemas: Array<Schema>, categories: Array<Category>) {
  // console.log('processSaveAssetJob', { job, schemas, categories });
  return new Promise((resolve, reject) => {
    resolve(job);
  });
}

export const startJob = createAsyncThunk('saveAssetJobs/startJob', async (payload: StartJobPayload, { dispatch, getState }) => {
  // console.log('startJob -----------', { payload });
  let toReturn: any = null;
  const { form, jobType, extraParams } = payload;
  let state = getState() as RootState;
  let { jobs } = state.saveAssetJobs;
  const job = new SaveAssetJob(form, jobType, extraParams, 0);
  dispatch(addJob(job));
  dispatch(updateIngestInProgress(true));
  // get latest state
  state = getState() as RootState;

  if (jobType === 'Subtitle' || jobType === 'Thumbnail') {
    toReturn = await dispatch(processSaveExternalSubtitleJob(payload));
  } else if (jobType === 'ChannelThumbnail') {
    toReturn = await dispatch(processSaveChannelThumbnailJob(payload));
  } else if (jobType === 'LiveProgramThumbnail') {
    toReturn = await dispatch(processSaveLiveProgramThumbnailJob(payload));
  } else {
    toReturn = await dispatch(processSaveAssetJob(job));
  }

  setTimeout(() => {
    state = getState() as RootState;
    jobs = state.saveAssetJobs.jobs;
    jobs.forEach((job) => {
      if (job.jobStatus === 'Completed') {
        dispatch(removeJob(job));
      }
    });
    state = getState() as RootState;
    jobs = state.saveAssetJobs.jobs;
    if (jobs.length === 0) {
      dispatch(clearJobs());
      dispatch(updateIngestInProgress(false));
    }
  }, 1500);

  // console.log('startJob', { toReturn });

  return toReturn;
});

export const batchUpload = createAsyncThunk(
  'saveAssetJobs/batchUpload',
  async (payload: BatchUploadJobPayload, { dispatch, getState }) => {
    const { forms, jobType, extraParams } = payload;
    let state = getState() as RootState;

    for (let i = 0; i < forms.length; i++) {
      const job = new SaveAssetJob(forms[i], jobType, extraParams, state.saveAssetJobs.jobs.length + i);
      dispatch(addJob(job));
    }
    state = getState() as RootState;
    let { jobs } = state.saveAssetJobs;
    // console.log('jobs', {jobs});
    dispatch(updateIngestInProgress(true));
    dispatch(updateBatchUploadEnable(true));

    const limit = pLimit(jobs.length > 50 ? 2 : 5);
    // Run multiple promise-returning & async functions with limited concurrency
    const promises = jobs.map((job) => limit(() => dispatch(processSaveAssetJob(job))));
    const result = await Promise.allSettled(promises);
    // console.log('result', { result, jobs });
    jobs.forEach((job) => {
      if (job.jobStatus === 'Completed') {
        dispatch(removeJob(job));
      }
    });
    if (result) {
      // clear out
      setTimeout(() => {
        state = getState() as RootState;
        jobs = state.saveAssetJobs.jobs;
        if (jobs.length === 0) {
          dispatch(clearJobs());
          limit.clearQueue();
          dispatch(updateBatchUploadEnable(false));
          dispatch(updateIngestInProgress(false));
        }
      }, 1500);
    }

    return result;
  }
);
